<div class="w-full h-full flex flex-col py-6 px-6">
  <!-- Options bar -->
  <div class="flex items-center gap-7 mb-7">
    <div class="flex items-center flex-wrap gap-2">
      <input type="checkbox" class="w-5 h-5 dark-checkbox" [(ngModel)]="full_report">
      <p class="text-white">Show Full Report</p>
    </div>
    <div class="flex items-center flex-wrap gap-2">
      <input type="checkbox" class="w-5 h-5 dark-checkbox" [(ngModel)]="mta_live_data">
      <p class="text-white">Include MTA Live Data Count</p>
    </div>
    <!--
      IT-F2-157 2026-10-30 — Billable vs Entitlement.
      ON  = cells show "distinct emails per (scanner, month) whose UserTracking
            grant has a non-fallback CommerceEvents row backing it". Pre-TTL
            months (older than CommerceEvents 365d TTL) render as "--".
      OFF = legacy "T + C derivation" — ever-touched entitlement count.
      Mike c/37bcd0e4 on IT-F2-432: billing can't count system-mistake grants.
    -->
    <div class="flex items-center flex-wrap gap-2"
         title="ON (default): count only users whose UserTracking grant has a non-fallback CommerceEvents row at or before end-of-month. OFF: legacy entitlement count (T+C derivation).">
      <input type="checkbox" class="w-5 h-5 dark-checkbox" [(ngModel)]="billable_only">
      <p class="text-white">Billable only <span class="text-emerald-400 font-mono">($)</span></p>
    </div>
    <!--
      IT-F2-157 2026-10-30 — Customer filter dropdown (Mike c/93e9c527).
      Pure-SPA filter — the full report is already loaded; we just hide
      non-selected customers. Empty string = show all. Options are built
      from grouped_scanners keys after get_billing_report() populates it.
    -->
    <div class="flex items-center flex-wrap gap-2">
      <label class="text-white/80 text-sm" for="customer-filter">Customer</label>
      <select id="customer-filter"
              class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm"
              [(ngModel)]="customer_filter"
              title="Focus the grid on a single customer. All customers = show every section.">
        <option value="">All customers</option>
        <option *ngFor="let c of customer_options" [value]="c">{{ c }}</option>
      </select>
    </div>
    <button type="button" class="ml-2 dark-btn min-w-[105px]" (click)="refresh()">
      <i class="fa-solid fa-fw fa-sync mr-1 dark:text-green-600"></i> Refresh
    </button>
    <!-- IT-F2-157 c/f900fc7f (Mike 2026-10-01): Copy Debug for the
         same diagnostic pattern as compliance-report. Dumps raw backend
         payload + per-customer row-key summary so missing rows are
         visible without a prod Mongo query. -->
    <!-- IT-F2-157 Mike c/cc5de0dc (2026-10-30): per-row select + Copy
         Debug scoped to selection. Button label carries the count. -->
    <button type="button" class="ml-2 dark-btn min-w-[105px]"
            [title]="selected_report_rows.size > 0
              ? ('Copy JSON snapshot scoped to ' + selected_report_rows.size + ' selected row(s)')
              : 'Copy a JSON snapshot of the raw backend payload + filters so a missing row is diagnosable without prod Mongo access'"
            (click)="copy_debug()">
      <i class="fa-solid fa-fw fa-clipboard mr-1 dark:text-sky-400"></i>
      Copy Debug{{ selected_report_rows.size > 0 ? ' (' + selected_report_rows.size + ')' : '' }}
    </button>
    <button *ngIf="selected_report_rows.size > 0"
            type="button" class="dark-btn text-xs"
            title="Clear row selection"
            (click)="clear_report_selection()">
      <i class="fa-solid fa-fw fa-xmark mr-1 dark:text-amber-300"></i> Clear ({{ selected_report_rows.size }})
    </button>
  </div>

  <!-- Data sources legend. Each pill = one source that feeds a subset
       of rows below. Hover reveals the exact backing collection / redis
       key so admins can trace any number back to its authority.
       Row-level source codes (T C P B L D) mirror this legend. -->
  <div class="flex items-center flex-wrap gap-2 mb-6 text-xs text-white/80">
    <span class="uppercase tracking-wide text-white/60 mr-1">Data sources</span>
    <span *ngFor="let src of sources"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-white/20 bg-white/5 cursor-help whitespace-pre-line"
          [title]="src.label + '\n\n' + src.location">
      <span class="font-mono font-semibold text-emerald-400">{{ src.code }}</span>
      <span>{{ src.label }}</span>
    </span>
  </div>

  <div class="w-full overflow-x-auto">
    <table class="billing-report w-full text-white">
      <tbody>
        <ng-container *ngFor="let customer of visible_grouped_scanners | keyvalue">
          <tr class="customer-row">
            <!-- IT-F2-157 Mike c/cc5de0dc (2026-10-30): empty cell above
                 the per-row checkboxes so the customer header row stays
                 aligned with the scanner rows below. -->
            <td class="w-6"></td>
            <td class="left text-lg font-semibold">
              {{ customer.key }}
              <span *ngFor="let c of source_codes_for(customer.key.toString(), '')"
                    class="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-mono font-semibold text-emerald-400 border border-white/25 rounded cursor-help align-middle whitespace-pre-line"
                    [title]="source_tooltip(c)">{{ c }}</span>
            </td>
            <td *ngFor="let month of month_keys" class="text-sm font-semibold">{{ format_month(month) }}</td>
          </tr>

          <tr *ngFor="let scanner of customer.value" class="hover:text-black">
            <!-- IT-F2-157 Mike c/cc5de0dc (2026-10-30): per-row checkbox
                 so Copy Debug can scope to specific lines for feedback. -->
            <td class="w-6">
              <input type="checkbox" class="w-4 h-4 dark-checkbox align-middle"
                     [checked]="is_report_row_selected(customer.key.toString(), scanner.scanner)"
                     (change)="toggle_report_row(customer.key.toString(), scanner.scanner, $event)"
                     [title]="'Select this row for Copy Debug scoping'" />
            </td>
            <!-- IT-F2-157 Mike c/97f291da (2026-10-30): row-title click
                 opens a 6-month aggregate breakout. Hover underline cues
                 the click; the sentinel-row tooltip still applies on
                 hover of the label itself. -->
            <td class="left scanner text-sm"
                [title]="scanner_tooltip(scanner.scanner)"
                [ngClass]="{'cursor-help': scanner_tooltip(scanner.scanner)}">
              <a [href]="breakout_url_range(customer.key.toString(), scanner.scanner)"
                 target="_blank" rel="noopener"
                 class="underline decoration-dotted decoration-white/25 underline-offset-2 hover:decoration-blue-400 hover:text-blue-300 cursor-pointer"
                 title="Open 6-month aggregate breakout for this scanner (last 6 month_keys)">
                {{ format_scanner_name(scanner.scanner) }}
              </a>
              <span *ngFor="let c of source_codes_for(customer.key.toString(), scanner.scanner)"
                    class="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-mono font-semibold text-emerald-400 border border-white/25 rounded cursor-help align-middle whitespace-pre-line"
                    [title]="source_tooltip(c)">{{ c }}</span>
            </td>
            <!-- IT-F2-157 item 10e3cea4 — each number cell is a link to
                 the breakout detail page (opens in a new tab so the
                 report stays open for another cell click). URL is
                 admin-gated at the SPA route + backend endpoint. -->
            <td *ngFor="let month of month_keys; let i = index" class="text-sm">
              <!-- IT-F2-157 2026-10-30 — null cells = pre-CommerceEvents-TTL
                   month in Billable mode (older than ~365d). Render as
                   "--" with a tooltip so the admin doesn't mistake it for
                   a zero. Still link to the breakout so the drill-down
                   shows the non-billable tray even for pre-TTL months. -->
              <a *ngIf="scanner.months[month] === null"
                 [href]="breakout_url(customer.key.toString(), scanner.scanner, month)"
                 target="_blank" rel="noopener"
                 title="Pre-CommerceEvents-TTL window — Billable count unverifiable. Toggle Billable off for the legacy entitlement count."
                 class="text-white/40 cursor-help">--</a>
              <ng-container *ngIf="scanner.months[month] !== null">
                <a [href]="breakout_url(customer.key.toString(), scanner.scanner, month)"
                   target="_blank" rel="noopener"
                   class="underline decoration-dotted decoration-white/25 underline-offset-2 hover:decoration-blue-400 hover:text-blue-300 cursor-pointer">
                  {{ (scanner.months[month] || 0) | number }}
                </a>

                <!-- Show change from previous month in parenthesis.
                     Skip when either side is a pre-TTL null — a (±N) delta
                     between a verified month and an unverifiable month
                     would be misleading. -->
                <span *ngIf="i > 0 && scanner.months[month_keys[i - 1]] !== null">
                  <ng-container
                    [ngTemplateOutlet]="changeTpl"
                    [ngTemplateOutletContext]="{
                      current: scanner.months[month] || 0,
                      previous: scanner.months[month_keys[i - 1]] || 0
                    }"
                  ></ng-container>
                </span>
              </ng-container>
            </td>
          </tr>

          <!-- Template to calculate and format month-to-month change -->
          <ng-template #changeTpl let-current="current" let-previous="previous">
            <span class="change" [ngClass]="{positive: current - previous > 0, negative: current - previous < 0}">
              ({{ current - previous > 0 ? '+' : '' }}{{ (current - previous) | number:'1.0-0' }})
            </span>
          </ng-template>

          <!-- Spacer -->
          <!-- IT-F2-157 Mike c/cc5de0dc (2026-10-30): colspan bumped
               by 1 to cover the new per-row-select checkbox column. -->
          <tr class="spacer-row h-10" *ngIf="customer.key !== 'TheoTrade'"><td [attr.colspan]="month_keys.length + 2"></td></tr>
        </ng-container>
      </tbody>
    </table>
  </div>
</div>